import { service } from '@ember/service'
import Component from '@glimmer/component'
import type { IntlService } from 'ember-intl'
import type { SeriesBarOptions, YAxisOptions } from 'highcharts'
import { colors } from '@colors'
import { SeriesBar } from 'teacher-ui-client/components/giraffe/primitives/series-bar'
import { ChartLegend } from 'teacher-ui-client/components/giraffe/chart-legend'
import type { WorkingAtGradeLevelData } from 'teacher-ui-client/utils/student-events/fetchers/working-at-grade-level'

interface Args {
  data: WorkingAtGradeLevelData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardWorkingAtGradeLevel extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Margins for SeriesBar
   */
  readonly chartMargin: number[] = [60, 10, 160, 10]

  /**
   * The input data for the chart, formatted for Highcharts.
   * Includes the colours to lock highcharts in to using those specific colours
   */
  get chartData(): SeriesBarOptions[] {
    const { below, at, above } = this.args.data
    return [
      { data: [{ y: below, color: colors.msGreen150 }], type: 'bar' },
      { data: [{ y: at, color: colors.msGreen300 }], type: 'bar' },
      { data: [{ y: above, color: colors.msGreen400 }], type: 'bar' },
    ]
  }

  /**
   * Data that will be provided to the legend for labels
   */
  get legendData() {
    const { intl } = this

    const belowLabel = intl.t('reporting.charts.dashboard.workingAtGradeLevel.belowLabel')
    const onLabel = intl.t('reporting.charts.dashboard.workingAtGradeLevel.onLabel')
    const aboveLabel = intl.t('reporting.charts.dashboard.workingAtGradeLevel.aboveLabel')

    const studentNumbersByKey = this.args.data
    const categories = [
      { key: 'below', label: belowLabel, color: colors.msGreen150 },
      { key: 'at', label: onLabel, color: colors.msGreen300 },
      { key: 'above', label: aboveLabel, color: colors.msGreen400 },
    ] as const

    return categories.map((category) => {
      const { key, label, color } = category
      const data = String(Math.round(studentNumbersByKey[key]))
      return { label, color, data }
    })
  }

  /**
   * The upper value of the highcharts chart. This makes sure the
   * chart values are properly aligned.
   */
  get yAxis(): YAxisOptions {
    const { below, at, above } = this.args.data
    const max = below + at + above

    return {
      startOnTick: false,
      endOnTick: false,
      gridLineWidth: 0,
      max,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      reversedStacks: false,
    }
  }

  <template>
    <div class="relative flex h-full grow flex-col" ...attributes>
      <div class="absolute inset-0">
        <SeriesBar
          @data={{this.chartData}}
          @xAxisVisible={{false}}
          @yAxisVisible={{false}}
          @yAxis={{this.yAxis}}
          @groupPadding={{0}}
          @chartMargin={{this.chartMargin}}
          @stackingStyle="normal"
          @maxPointWidth={{120}}
          @plotBackgroundColor="transparent"
          @borderWidth={{1}}
        />
        <ChartLegend
          {{! 80px to match highcharts chartMargin top }}
          class="absolute inset-0 top-auto h-[80px] px-6"
          @data={{this.legendData}}
          @labelsBelow={{true}}
        />
      </div>
    </div>
  </template>
}

export default DashboardWorkingAtGradeLevel
